import React, { useState, useEffect } from "react";
import { FaPercent, FaSave, FaPlus, FaEdit, FaTrash, FaBuilding, FaUsers, FaChartLine, FaInfoCircle, FaArrowRight, FaTrophy, FaExclamationTriangle, FaEye, FaCog, FaHandshake, FaList } from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";

const UnifiedCommissionPanel = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Commission settings state
  const [globalCommission, setGlobalCommission] = useState(10);
  const [gstPercentage, setGstPercentage] = useState(18);
  const [customCommissions, setCustomCommissions] = useState([]);
  const [transparencyData, setTransparencyData] = useState({});
  const [vendors, setVendors] = useState([]);
  
  // Forms state
  const [showAddForm, setShowAddForm] = useState(false);
  const [showVendorCommissionForm, setShowVendorCommissionForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const [formData, setFormData] = useState({
    type: "global",
    percentage: 10,
    driverId: "",
    cabTypeId: "",
    vendorId: "",
    description: "",
  });

  const [vendorCommissionData, setVendorCommissionData] = useState({
    vendorId: "",
    percentage: 10,
    description: ""
  });

  const { API_URL } = require("../../config/apiConfig");

  // Suppressed error messages specific to this panel
  const suppressedErrors = [
    /failed to load commission settings/i,
    /failed to load transparency data/i,
  ];

  const showError = (msg) => {
    const text = String(msg || "");
    if (!suppressedErrors.some((re) => re.test(text))) {
      toast.error(text);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  useEffect(() => {
    fetchCommissionSettings();
    fetchTransparencyData();
    fetchVendors();
    fetchGSTPercentage();
  }, []);

  const fetchGSTPercentage = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const token = user?.token;

      if (!token) return;

      const response = await axios.get(`${API_URL}/admin/booking-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success || response.data.status) {
        const gst = response.data.data?.gstPercentage || 18;
        setGstPercentage(gst);
      }
    } catch (error) {
      console.error("Error fetching GST:", error);
      setGstPercentage(18); // Default fallback
    }
  };

  const fetchCommissionSettings = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const token = user?.token;

      if (!token) {
        toast.error("Authentication required. Please log in.");
        return;
      }

      const response = await axios.get(`${API_URL}/commission/admin/commission-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status) {
        const settings = response.data.data;
        
        // Extract global commission
        if (global) setGlobalCommission(global.percentage);
        
        // Extract custom commissions (excluding global)
        const custom = settings.filter(s => s.type !== "global");
        setCustomCommissions(custom);
      }
    } catch (error) {
      console.error("Error fetching commission settings:", error);
      showError("Failed to load commission settings");
    }
  };

  const fetchTransparencyData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const token = user?.token;

      if (!token) {
        toast.error("Authentication required. Please log in.");
        return;
      }

      const response = await axios.get(`${API_URL}/commission/admin/transparency`, {
        headers: { Authorization: `Bearer ${token}` }
      });


      // Handle both response formats (status or success)
      const isSuccess = response.data.status === true || response.data.success === true;

      if (isSuccess) {
        setTransparencyData(response.data.data || {});
      } else {
      }
    } catch (error) {
      console.error("❌ Error fetching transparency data:", error);
      console.error("❌ Error response:", error.response?.data);
      showError("Failed to load transparency data");
    }
  };

  const fetchVendors = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const token = user?.token;

      if (!token) {
        console.error("❌ No token found in localStorage");
        toast.error("Authentication required. Please log in.");
        return;
      }

      const response = await axios.get(`${API_URL}/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });


      if (response.data.status) {
        const allVendors = response.data.data?.vendors || response.data.data || [];
        const approvedVendors = allVendors.filter(v => v.verificationStatus === 'approved');
        setVendors(approvedVendors);
      } else {
        console.error("❌ API call unsuccessful:", response.data.message);
      }
    } catch (error) {
      console.error("❌ Error fetching vendors:", error);
      console.error("❌ Error response:", error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalCommissionSave = async () => {
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const token = user?.token;

      if (!token) {
        toast.error("Authentication required. Please log in.");
        return;
      }

      const response = await axios.post(`${API_URL}/commission/admin/commission-settings`, {
        type: "global",
        percentage: globalCommission,
        description: `Global commission rate: ${globalCommission}%`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status) {
        toast.success("Global commission updated successfully!");
        fetchCommissionSettings();
        fetchTransparencyData();
      }
    } catch (error) {
      console.error("Error updating global commission:", error);
      toast.error("Failed to update global commission");
    } finally {
      setSaving(false);
    }
  };

  const handleVendorCommissionSave = async () => {
    console.log("🚀 Starting commission save process...");
    console.log("📊 Current vendorCommissionData:", vendorCommissionData);

    if (!vendorCommissionData.vendorId || vendorCommissionData.percentage < 0) {
      console.log("��� Validation failed - vendorId:", vendorCommissionData.vendorId, "percentage:", vendorCommissionData.percentage);
      toast.error("Please select a vendor and enter a valid commission percentage");
      return;
    }

    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const token = user?.token;

      console.log("🔐 User data:", user ? "Found" : "Not found");
      console.log("🎫 Token:", token ? "Present" : "Missing");

      if (!token) {
        console.log("❌ No authentication token found");
        toast.error("Authentication required. Please log in.");
        return;
      }

      const requestUrl = `${API_URL}/commission/admin/vendor/${vendorCommissionData.vendorId}/set-admin-commission`;
      const requestData = {
        percentage: vendorCommissionData.percentage,
        description: vendorCommissionData.description || `Admin commission for vendor: ${vendorCommissionData.percentage}%`
      };

      console.log("📤 Making API request:");
      console.log("   URL:", requestUrl);
      console.log("   Data:", requestData);
      console.log("   Token (first 20 chars):", token.substring(0, 20) + "...");

      const response = await axios.post(requestUrl, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log("📥 API Response received:");
      console.log("   Status:", response.status);
      console.log("   Response data:", response.data);
      console.log("   Response.data.status:", response.data.status);
      console.log("   Response.data.success:", response.data.success);
      console.log("   Response.data.message:", response.data.message);

      // Handle both new (status) and old (success) response formats
      const isSuccess = response.data.status === true || response.data.success === true;

      console.log("🔍 Final success check:", isSuccess);

      if (isSuccess) {
        console.log("✅ Commission save successful!");
        toast.success(`Admin commission set to ${vendorCommissionData.percentage}% for vendor!`);
        setShowVendorCommissionForm(false);
        setVendorCommissionData({ vendorId: "", percentage: 10, description: "" });

        // Refresh all data sources to update the vendor commission list
        console.log("🔄 Refreshing commission data...");

        // Show loading message and set refreshing state
        setRefreshing(true);
        toast.info("Refreshing commission data...", { autoClose: 1000 });

        // Aggressive retry mechanism for transparency data
        const retryTransparencyData = async (attempts = 3) => {
          for (let i = 0; i < attempts; i++) {
            console.log(`🔄 Retry attempt ${i + 1}/${attempts} for transparency data...`);
            await new Promise(resolve => setTimeout(resolve, (i + 1) * 500)); // Increasing delay

            try {
              await fetchTransparencyData();

              // Check if transparency data now has vendors
              if (transparencyData.vendors && transparencyData.vendors.length > 0) {
                console.log("✅ Transparency data successfully updated!");
                return true;
              }
            } catch (error) {
              console.log(`❌ Retry ${i + 1} failed:`, error);
            }
          }
          return false;
        };

        // Execute refresh with retry logic
        setTimeout(async () => {
          try {
            // First do the regular refresh
            await Promise.all([
              fetchCommissionSettings(),
              fetchVendors()
            ]);

            // Then retry transparency data with aggressive retry
            const transparencySuccess = await retryTransparencyData(3);

            if (transparencySuccess) {
              console.log("✅ All data refreshed successfully!");
              toast.success("Commission list updated!", { autoClose: 2000 });
            } else {
              console.log("⚠️ Transparency data still not updated after retries");
              toast.warning("Commission saved! If list doesn't update, please refresh the page.");

              // Force one more manual refresh
              setTimeout(() => {
                fetchTransparencyData();
              }, 2000);
            }
          } catch (refreshError) {
            console.error("❌ Error refreshing data:", refreshError);
            toast.warning("Commission saved but data refresh failed. Please refresh the page.");
          } finally {
            setRefreshing(false);
          }
        }, 500); // Initial delay to ensure backend processing is complete
      } else {
        console.log("❌ Commission save failed - neither status nor success is true");
        console.log("   response.data.status:", response.data.status);
        console.log("   response.data.success:", response.data.success);
        toast.error(response.data.message || "Commission save failed");
      }
    } catch (error) {
      console.error("�� Error setting vendor commission:", error);
      console.error("❌ Error response:", error.response?.data);
      console.error("❌ Error status:", error.response?.status);
      console.error("❌ Error message:", error.message);
      toast.error(error.response?.data?.message || "Failed to set vendor commission");
    } finally {
      setSaving(false);
      console.log("🏁 Commission save process completed");
    }
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Commission Flow Diagram */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <FaChartLine className="mr-2 text-blue-500" />
          Commission Flow Overview
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* One-Tier Flow */}
          <div className="border rounded-lg p-4 bg-blue-50">
            <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
              <FaArrowRight className="mr-2" />
              One-Tier: Admin ↔ Driver
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Admin Commission:</span>
                <span className="font-semibold text-blue-600">{globalCommission}%</span>
              </div>
              <div className="flex justify-between">
                <span>GST (Added to Admin):</span>
                <span className="font-semibold text-orange-600">{gstPercentage}%</span>
              </div>
              <div className="flex justify-between">
                <span>Driver Earnings:</span>
                <span className="font-semibold text-green-600">{100 - globalCommission - gstPercentage}%</span>
              </div>
              <div className="border-t pt-2 flex justify-between font-semibold">
                <span>Total Admin Deduction:</span>
                <span className="text-red-600">{globalCommission + gstPercentage}%</span>
              </div>
            </div>
          </div>

          {/* Two-Tier Flow */}
          <div className="border rounded-lg p-4 bg-green-50">
            <h4 className="font-semibold text-green-800 mb-3 flex items-center">
              <FaHandshake className="mr-2" />
              Two-Tier: Admin ↔ Vendor ↔ Driver
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Admin Commission:</span>
                <span className="font-semibold text-blue-600">Variable per vendor</span>
              </div>
              <div className="flex justify-between">
                <span>GST (Added to Admin):</span>
                <span className="font-semibold text-orange-600">{gstPercentage}%</span>
              </div>
              <div className="flex justify-between">
                <span>Vendor Commission:</span>
                <span className="font-semibold text-purple-600">Set by vendor</span>
              </div>
              <div className="flex justify-between">
                <span>Driver Earnings:</span>
                <span className="font-semibold text-green-600">After all deductions</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {transparencyData.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Vendors</p>
                <p className="text-2xl font-bold text-gray-900">{transparencyData.summary.totalVendors}</p>
              </div>
              <FaBuilding className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Custom Admin Rates</p>
                <p className="text-2xl font-bold text-gray-900">{transparencyData.summary.customAdminRates}</p>
              </div>
              <FaCog className="h-8 w-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Admin Rate</p>
                <p className="text-2xl font-bold text-gray-900">{transparencyData.summary.avgAdminRate?.toFixed(1)}%</p>
              </div>
              <FaPercent className="h-8 w-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Vendor Rate</p>
                <p className="text-2xl font-bold text-gray-900">{transparencyData.summary.avgVendorRate?.toFixed(1)}%</p>
              </div>
              <FaHandshake className="h-8 w-8 text-orange-500" />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderGlobalSettingsTab = () => (
    <div className="space-y-6">
      {/* Global Commission Settings */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <FaPercent className="mr-2 text-blue-500" />
          Global Admin Commission
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Admin Commission Rate (%)
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="number"
                min="0"
                max="50"
                step="0.1"
                value={globalCommission}
                onChange={(e) => setGlobalCommission(parseFloat(e.target.value) || 0)}
                className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter commission percentage"
              />
              <button
                onClick={handleGlobalCommissionSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                <FaSave className="mr-2" />
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              This rate applies to all admin drivers and as fallback for vendors without custom rates.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3">Impact Preview (₹1000 Booking)</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between p-2 bg-white rounded">
                <span>Admin Commission ({globalCommission}%):</span>
                <span className="font-semibold text-blue-600">{formatCurrency(1000 * globalCommission / 100)}</span>
              </div>
              <div className="flex justify-between p-2 bg-white rounded">
                <span>GST ({gstPercentage}%):</span>
                <span className="font-semibold text-orange-600">{formatCurrency(1000 * gstPercentage / 100)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between p-2 bg-red-50 rounded font-semibold">
                <span>Total Admin Gets:</span>
                <span className="text-red-600">{formatCurrency(1000 * (globalCommission + gstPercentage) / 100)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between p-2 bg-green-50 rounded font-semibold">
                <span>Driver Gets:</span>
                <span className="text-green-600">{formatCurrency(1000 * (100 - globalCommission - gstPercentage) / 100)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Commission Settings */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <FaUsers className="mr-2 text-green-500" />
            Custom Commission Settings
          </h3>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
          >
            <FaPlus className="mr-2" />
            Add Custom Rate
          </button>
        </div>

        {customCommissions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customCommissions.map((commission) => (
                  <tr key={commission._id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {commission.type.replace('_', ' ').toUpperCase()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {commission.applicableFor?.driverId?.name || 
                       commission.applicableFor?.cabTypeId?.name || 
                       commission.applicableFor?.vendorId?.fullName || 
                       'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                      {commission.percentage}%
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {commission.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button className="text-blue-600 hover:text-blue-900">
                        <FaEdit />
                      </button>
                      <button className="text-red-600 hover:text-red-900">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FaInfoCircle className="mx-auto text-4xl mb-4" />
            <p>No custom commission settings configured</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderVendorCommissionTab = () => (
    <div className="space-y-6">
      {/* Add Vendor Commission Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <FaBuilding className="mr-2 text-purple-500" />
            Admin Commission per Vendor
          </h3>
          <button
            onClick={() => setShowVendorCommissionForm(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
          >
            <FaPlus className="mr-2" />
            Set Vendor Commission
          </button>
        </div>

        <div className="mb-4 space-y-3">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 font-semibold mb-2">
              <FaInfoCircle className="inline mr-2" />
              Commission Breakdown
            </p>
            <p className="text-sm text-blue-700">
              Set different admin commission rates for each vendor. The actual deduction to drivers includes GST ({gstPercentage}%) added to the admin commission.
            </p>
          </div>
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800 font-semibold mb-2">
              <FaExclamationTriangle className="inline mr-2" />
              GST Calculation Example
            </p>
            <div className="text-sm text-amber-700 space-y-1">
              <div>If admin commission = 10% + GST = {gstPercentage}%</div>
              <div>Total deduction from driver = 10% + {gstPercentage}% = {10 + gstPercentage}%</div>
              <div>For ₹1000 booking: Admin gets ₹{Math.round(1000 * (10 + gstPercentage) / 100)}, Driver gets ₹{Math.round(1000 * (100 - 10 - gstPercentage) / 100)}</div>
            </div>
          </div>
        </div>

        {/* Vendor Commission Form Modal */}
        {showVendorCommissionForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h4 className="text-lg font-semibold mb-4">Set Admin Commission for Vendor</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Vendor</label>
                  <select
                    value={vendorCommissionData.vendorId}
                    onChange={(e) => setVendorCommissionData({...vendorCommissionData, vendorId: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">Choose a vendor...</option>
                    {vendors.map(vendor => (
                      <option key={vendor._id} value={vendor._id}>
                        {vendor.fullName || vendor.businessName} ({vendor.email})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Admin Commission Rate (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    step="0.1"
                    value={vendorCommissionData.percentage}
                    onChange={(e) => setVendorCommissionData({...vendorCommissionData, percentage: parseFloat(e.target.value) || 0})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter admin commission percentage"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
                  <textarea
                    value={vendorCommissionData.description}
                    onChange={(e) => setVendorCommissionData({...vendorCommissionData, description: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    rows="3"
                    placeholder="Optional description for this commission setting"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowVendorCommissionForm(false);
                    setVendorCommissionData({ vendorId: "", percentage: 10, description: "" });
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVendorCommissionSave}
                  disabled={saving}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Commission"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Vendor Commission List */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <FaList className="mr-2 text-blue-500" />
            Current Vendor Commission Settings
          </h3>
          <button
            onClick={async () => {
              setRefreshing(true);
              toast.info("Refreshing vendor commission data...");
              try {
                await Promise.all([fetchTransparencyData(), fetchVendors()]);
                toast.success("Data refreshed!");
              } catch (error) {
                toast.error("Failed to refresh data");
              } finally {
                setRefreshing(false);
              }
            }}
            disabled={refreshing}
            className={`px-3 py-1 text-sm rounded transition-colors flex items-center ${
              refreshing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            {refreshing ? '⏳ Refreshing...' : '🔄 Refresh List'}
          </button>
        </div>

        {(transparencyData.vendors && transparencyData.vendors.length > 0) ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Deduction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Drivers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transparencyData.vendors.map((vendor) => (
                  <tr key={vendor.vendor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <FaBuilding className="text-purple-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {vendor.vendor.fullName || vendor.vendor.businessName}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {vendor.vendor.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vendor.vendor.email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-lg font-bold text-blue-600">
                          {vendor.commissionStructure.adminRate}%
                        </span>
                        {vendor.commissionStructure.isCustomAdminRate && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Custom Rate
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-orange-600">{gstPercentage}%</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-red-600">{vendor.commissionStructure.adminRate + gstPercentage}%</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <FaUsers className="mr-1 text-gray-400" />
                        {vendor.vendor.totalDrivers || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => {
                          setVendorCommissionData({
                            vendorId: vendor.vendor.id,
                            percentage: vendor.commissionStructure.adminRate,
                            description: `Admin commission for ${vendor.vendor.fullName || vendor.vendor.businessName}`
                          });
                          setShowVendorCommissionForm(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <FaEdit className="mr-1" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center py-8 text-gray-500">
              <FaInfoCircle className="mx-auto text-4xl mb-4" />
              <p className="text-lg font-medium">No vendor commission settings configured</p>
              <p className="text-sm mt-2">Click "Set Vendor Commission" to add your first vendor commission rate</p>

              {vendors.length > 0 && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 max-w-md mx-auto">
                  <p className="text-sm text-blue-800">
                    <strong>✅ {vendors.length} vendors available</strong> - Ready to set commission rates!
                  </p>
                  <div className="mt-2 text-xs text-blue-600">
                    {vendors.slice(0, 3).map(v => v.fullName || v.businessName).join(', ')}
                    {vendors.length > 3 && ` and ${vendors.length - 3} more...`}
                  </div>
                  <button
                    onClick={async () => {
                      toast.info("Force refreshing transparency data...");
                      setRefreshing(true);
                      try {
                        await fetchTransparencyData();
                        if (transparencyData.vendors && transparencyData.vendors.length > 0) {
                          toast.success("Found commission data!");
                        } else {
                          toast.warning("Still no commission data found");
                        }
                      } catch (error) {
                        toast.error("Refresh failed");
                      } finally {
                        setRefreshing(false);
                      }
                    }}
                    className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    🔄 Force Refresh Commission Data
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderTransparencyTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <FaEye className="mr-2 text-green-500" />
          Commission Transparency
        </h3>
        
        {transparencyData.vendors ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Deduction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Drivers</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transparencyData.vendors.map((vendor) => (
                  <tr key={vendor.vendor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {vendor.vendor.fullName || vendor.vendor.businessName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {vendor.commissionStructure.isCustomAdminRate && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Custom Admin Rate
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                      {vendor.commissionStructure.adminRate}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-orange-600">
                      {gstPercentage}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-purple-600">
                      {vendor.commissionStructure.vendorRate}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                      {100 - vendor.commissionStructure.adminRate - gstPercentage - vendor.commissionStructure.vendorRate}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600 bg-red-50 px-3 py-2 rounded">
                      {vendor.commissionStructure.adminRate + gstPercentage + vendor.commissionStructure.vendorRate}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vendor.vendor.totalDrivers || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FaInfoCircle className="mx-auto text-4xl mb-4" />
            <p>Loading transparency data...</p>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: FaChartLine },
            { id: 'global', label: 'Global Settings', icon: FaCog },
            { id: 'vendor', label: 'Vendor Commission', icon: FaBuilding },
            { id: 'transparency', label: 'Transparency', icon: FaEye }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'global' && renderGlobalSettingsTab()}
        {activeTab === 'vendor' && renderVendorCommissionTab()}
        {activeTab === 'transparency' && renderTransparencyTab()}
      </div>
    </div>
  );
};

export default UnifiedCommissionPanel;
